import { useState, useRef } from 'react';
import { formatFileSize } from '@/mocks/driveFiles';
import type { DriveItem } from '@/mocks/driveFiles';

interface DriveSidebarProps {
  currentFolderId: string | null;
  dragOverFolderId: string | null;
  usedStorage: number;
  totalStorage: number;
  onFolderSelect: (folderId: string | null) => void;
  allFiles: DriveItem[];
  onDragOver: (e: React.DragEvent, folderId: string | null) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, folderId: string | null) => void;
  onDragEnd: () => void;
}

export default function DriveSidebar({
  currentFolderId,
  dragOverFolderId,
  usedStorage,
  totalStorage,
  onFolderSelect,
  allFiles,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: DriveSidebarProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['folder-4']));
  const autoExpandTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleExpand = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const triggerAutoExpand = (folderId: string) => {
    if (autoExpandTimer.current) clearTimeout(autoExpandTimer.current);
    autoExpandTimer.current = setTimeout(() => {
      setExpandedFolders((prev) => {
        if (prev.has(folderId)) return prev;
        const next = new Set(prev);
        next.add(folderId);
        return next;
      });
    }, 600);
  };

  const cancelAutoExpand = () => {
    if (autoExpandTimer.current) {
      clearTimeout(autoExpandTimer.current);
      autoExpandTimer.current = null;
    }
  };

  const handleSidebarDragLeave = (e: React.DragEvent) => {
    cancelAutoExpand();
    onDragLeave(e);
  };

  const handleSidebarDrop = (e: React.DragEvent, folderId: string | null) => {
    cancelAutoExpand();
    onDrop(e, folderId);
  };

  const handleDragEnd = () => {
    cancelAutoExpand();
    onDragEnd();
  };

  const rootFolders = allFiles.filter((f) => f.type === 'folder' && f.parentId === null && !f.deletedAt);
  const getSubfolders = (parentId: string) =>
    allFiles.filter((f) => f.type === 'folder' && f.parentId === parentId && !f.deletedAt);

  const trashItems = allFiles.filter((f) => f.deletedAt);
  const isTrashActive = currentFolderId === '__trash__';

  const renderFolderTree = (folders: DriveItem[], depth: number = 0) => {
    return folders.map((folder) => {
      const subfolders = getSubfolders(folder.id);
      const isExpanded = expandedFolders.has(folder.id);
      const isActive = currentFolderId === folder.id;
      const isDragOver = dragOverFolderId === folder.id;

      return (
        <div key={folder.id}>
          <button
            onClick={() => {
              onFolderSelect(folder.id);
              toggleExpand(folder.id);
            }}
            onDragOver={(e) => {
              onDragOver(e, folder.id);
              triggerAutoExpand(folder.id);
            }}
            onDragLeave={handleSidebarDragLeave}
            onDrop={(e) => handleSidebarDrop(e, folder.id)}
            onDragEnd={handleDragEnd}
            className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all duration-150 cursor-pointer group whitespace-nowrap ${
              isDragOver
                ? 'bg-accent-100 text-accent-800 ring-2 ring-accent-300 scale-[1.02]'
                : isActive
                  ? 'bg-primary-100 text-primary-800'
                  : 'text-foreground-700 hover:bg-background-200'
            }`}
            style={{ paddingLeft: `${12 + depth * 16}px` }}
          >
            <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${
              subfolders.length > 0 && isExpanded ? 'rotate-90' : ''
            }`}>
              {subfolders.length > 0 ? (
                <i className="ri-arrow-right-s-line text-xs"></i>
              ) : (
                <span className="w-3"></span>
              )}
            </span>
            <span className="w-5 h-5 flex items-center justify-center relative">
              <i className={`ri-folder-3-line text-base ${
                isDragOver ? 'text-accent-600' : isActive ? 'text-primary-600' : 'text-secondary-500'
              }`}></i>
              {isDragOver && (
                <span className="absolute inset-0 flex items-center justify-center">
                  <i className="ri-folder-transfer-line text-xs text-accent-600"></i>
                </span>
              )}
            </span>
            <span className="truncate flex-1 text-left">{folder.name}</span>
            {isDragOver && (
              <span className="text-xs text-accent-600 opacity-80">
                <i className="ri-arrow-down-line"></i>
              </span>
            )}
          </button>
          {isExpanded && subfolders.length > 0 && (
            <div>{renderFolderTree(subfolders, depth + 1)}</div>
          )}
        </div>
      );
    });
  };

  const usedPercent = totalStorage > 0 ? (usedStorage / totalStorage) * 100 : 0;

  const isRootDragOver = dragOverFolderId === null;

  return (
    <aside className="w-full lg:w-[240px] flex flex-col border-r border-background-200 bg-background-50 h-full">
      <div className="p-4 border-b border-background-200">
        <button
          onClick={() => onFolderSelect(null)}
          onDragOver={(e) => {
            onDragOver(e, null);
            cancelAutoExpand();
          }}
          onDragLeave={handleSidebarDragLeave}
          onDrop={(e) => handleSidebarDrop(e, null)}
          onDragEnd={handleDragEnd}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 cursor-pointer whitespace-nowrap ${
            isRootDragOver
              ? 'bg-accent-100 text-accent-800 ring-2 ring-accent-300 scale-[1.02]'
              : currentFolderId === null
                ? 'bg-primary-100 text-primary-800'
                : 'text-foreground-700 hover:bg-background-200'
          }`}
        >
          <span className="w-5 h-5 flex items-center justify-center relative">
            <i className={`ri-hard-drive-2-line ${isRootDragOver ? 'text-accent-600' : ''}`}></i>
            {isRootDragOver && (
              <span className="absolute inset-0 flex items-center justify-center">
                <i className="ri-folder-transfer-line text-xs text-accent-600"></i>
              </span>
            )}
          </span>
          <span>내 드라이브</span>
          {isRootDragOver && (
            <span className="text-xs text-accent-600 opacity-80 ml-auto">
              <i className="ri-arrow-down-line"></i>
            </span>
          )}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        <p className="px-3 py-1.5 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
          폴더
        </p>
        {renderFolderTree(rootFolders)}
      </nav>

      <div className="border-t border-background-200 py-2 px-3">
        <button
          onClick={() => onFolderSelect('__trash__' as string | null)}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 cursor-pointer whitespace-nowrap ${
            isTrashActive
              ? 'bg-accent-100 text-accent-800'
              : 'text-foreground-600 hover:bg-accent-50 hover:text-accent-700'
          }`}
        >
          <span className={`w-5 h-5 flex items-center justify-center ${isTrashActive ? 'text-accent-600' : 'text-foreground-400'}`}>
            <i className="ri-delete-bin-line"></i>
          </span>
          <span>휴지통</span>
          {trashItems.length > 0 && (
            <span className={`ml-auto text-xs font-semibold px-1.5 py-0.5 rounded-full ${
              isTrashActive
                ? 'bg-accent-200 text-accent-800'
                : 'bg-background-200 text-foreground-500'
            }`}>
              {trashItems.length}
            </span>
          )}
        </button>
      </div>

      <div className="p-4 border-t border-background-200">
        <div className="mb-2 flex items-center justify-between text-xs text-foreground-600">
          <span>저장공간</span>
          <span>{formatFileSize(usedStorage)} / {formatFileSize(totalStorage)}</span>
        </div>
        <div className="h-2 rounded-full bg-background-200 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ease-out ${
              usedPercent > 90 ? 'bg-accent-500' : 'bg-primary-500'
            }`}
            style={{ width: `${Math.min(usedPercent, 100)}%` }}
          ></div>
        </div>
        <p className="mt-1.5 text-xs text-foreground-500">
          {formatFileSize(totalStorage - usedStorage)} 남음
        </p>
      </div>
    </aside>
  );
}